import { Link } from "react-router-dom";
import {
  GitBranch, ChefHat, UtensilsCrossed, Package, Wrench, Sparkles, Hotel,
  Banknote, Radar, BellRing, Cog, ExternalLink, CheckCircle2, Pin, Bookmark, Bell,
} from "lucide-react";
import { PRIORITY_STYLE, MODULE_META, timeAgo } from "./opsApi";

const ICON_MAP = { GitBranch, ChefHat, UtensilsCrossed, Package, Wrench, Sparkles, Hotel, Banknote, Radar, BellRing, Cog };

const ENTITY_LINK = {
  wae_request:      (id) => `/workflow/inbox?open=${id}`,
  kops_request:     ()   => `/kitchen/requests`,
  kops_waste:       ()   => `/kitchen/waste`,
  supply_item:      ()   => `/supply/stock`,
  ops_task:         ()   => `/operations-hub/tasks`,
  ops_announcement: ()   => `/operations-hub/announcements`,
  ops_broadcast:    ()   => `/operations-hub/broadcasts`,
};

export default function EventCard({ e, actions, testid, compact = false }) {
  const meta = MODULE_META[e.module] || MODULE_META.sys;
  const Icon = ICON_MAP[meta.icon] || BellRing;
  const link = ENTITY_LINK[e.entity_kind]?.(e.entity_id);

  return (
    <div data-testid={testid} className={`flex gap-3 ${compact ? "px-3 py-2" : "px-4 py-3"} hover:bg-slate-50/60 transition group`}>
      <div className={`w-9 h-9 rounded-xl bg-slate-900/95 ring-1 ring-white/10 flex items-center justify-center ${meta.accent} shrink-0`}>
        <Icon className="w-4 h-4" strokeWidth={1.75}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10.5px] uppercase tracking-wider font-semibold text-slate-400">{meta.label}</span>
          <span className={`px-1.5 py-0.5 rounded-md text-[10px] font-medium capitalize ${PRIORITY_STYLE[e.priority] || "bg-slate-100 text-slate-600"}`}>{e.priority}</span>
          {e.department && <span className="text-[10.5px] mono text-slate-400">· {e.department}</span>}
          <span className="text-[10.5px] mono text-slate-400">· {e.kind}</span>
        </div>
        <div className="text-[13px] font-medium text-hocp-navy mt-0.5 truncate">{e.title}</div>
        {e.description && <div className="text-[12px] text-slate-500 mt-0.5 line-clamp-2">{e.description}</div>}
        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-400">
          <span className="mono">{timeAgo(e.at)}</span>
          {e.actor_email && <span className="mono truncate max-w-[180px]">by {e.actor_email}</span>}
          {e.entity_ref && <span className="mono truncate max-w-[140px]">· {e.entity_ref}</span>}
        </div>
      </div>
      <div className="flex items-start gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition">
        {link && (
          <Link to={link} data-testid={`${testid}-open`}
                className="h-7 px-2 rounded-lg text-[11px] text-slate-500 hover:text-hocp-navy hover:bg-slate-100 inline-flex items-center gap-1">
            <ExternalLink className="w-3 h-3"/> Open
          </Link>
        )}
        {actions}
      </div>
    </div>
  );
}

export function NotifActions({ n, onAction }) {
  return (
    <div className="flex items-start gap-1">
      {!n.acknowledged && (
        <button onClick={()=>onAction(n.id,"ack")} data-testid={`ops-notif-ack-${n.id}`}
                className="h-7 w-7 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-emerald-600 inline-flex items-center justify-center" title="Acknowledge">
          <CheckCircle2 className="w-3.5 h-3.5"/>
        </button>
      )}
      <button onClick={()=>onAction(n.id, n.pinned ? "unpin" : "pin")} data-testid={`ops-notif-pin-${n.id}`}
              className={`h-7 w-7 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center ${n.pinned ? "text-hocp-gold" : "text-slate-500"}`} title="Pin">
        <Pin className="w-3.5 h-3.5"/>
      </button>
      <button onClick={()=>onAction(n.id, n.bookmarked ? "unbookmark" : "bookmark")} data-testid={`ops-notif-bookmark-${n.id}`}
              className={`h-7 w-7 rounded-lg hover:bg-slate-100 inline-flex items-center justify-center ${n.bookmarked ? "text-sky-600" : "text-slate-500"}`} title="Bookmark">
        <Bookmark className="w-3.5 h-3.5"/>
      </button>
      {n.state !== "archived" && (
        <button onClick={()=>onAction(n.id,"archive")} data-testid={`ops-notif-archive-${n.id}`}
                className="h-7 px-2 rounded-lg text-[11px] text-slate-500 hover:bg-slate-100">Archive</button>
      )}
      {n.state === "unread" && (
        <button onClick={()=>onAction(n.id,"read")} data-testid={`ops-notif-read-${n.id}`}
                className="h-7 px-2 rounded-lg text-[11px] text-slate-500 hover:bg-slate-100">Mark read</button>
      )}
    </div>
  );
}
